import { useEffect, useRef, useState, useCallback } from 'react';

// Hook for handling data loading with caching
export function useDataLoader(
  loadFn: () => Promise<void>,
  options = { reloadOnFocus: true }
) {
  const loadingRef = useRef(false);
  const timeoutRef = useRef<number>();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (loadingRef.current || !mountedRef.current) return;
      
      try {
        loadingRef.current = true;
        await loadFn();
      } catch (error) {
        console.error('Data loading error:', error);
      } finally {
        if (mountedRef.current) {
          loadingRef.current = false;
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && options.reloadOnFocus) {
        // Clear any pending timeout
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }

        // Debounce the reload
        timeoutRef.current = window.setTimeout(() => {
          if (mountedRef.current) {
            load();
          }
        }, 1000);
      }
    };

    // Load data initially
    load();

    // Set up visibility change listener if reloadOnFocus is enabled
    if (options.reloadOnFocus) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [loadFn, options.reloadOnFocus]);
}

// Export other hooks...
export * from './hooks';